const sharedApiBaseUrl =
  process.env.REACT_APP_API_BASE_URL || "http://192.168.2.4:3001/api";
const sharedStorageBaseUrl =
  process.env.REACT_APP_STORAGE_BASE_URL || "http://192.168.2.4:3000/storage";

export const DEFAULT_CLIENT_ID = "liana-fashion";
export const DEFAULT_WHATSAPP_PHONE_NUMBER = "972599879227";
export const DEFAULT_CHECKOUT_ORDER_DEFAULTS = Object.freeze({
  orderType: "online",
  shippingStatus: "processing",
  paymentStatus: "unpaid",
  phoneExtensionDefault: "961",
});
export const DEFAULT_CHECKOUT_WHATSAPP_DISPLAY = Object.freeze({
  displayInitials: "LF",
  displayName: "Liana Fashion",
  displayPhoneText: "59-987-9227 972+",
});
export const DEFAULT_CONTACT_INFO = Object.freeze({
  info: "",
  location: "Palestine",
  phoneDisplay: "+961 1 234 567",
  phoneDial: "+9611234567",
  email: "info@store.com",
  workingHours: "Saturday - Thursday",
  workingTime: "9:00 AM - 9:00 PM",
});

const buildTimeClientId = (process.env.REACT_APP_CLIENT_ID || "").trim();
const clientIdOverrideStorageKey = "clientIdOverride";

export const CLIENT_CONFIG_MAP = Object.freeze({
  "liana-fashion": Object.freeze({
    clientId: "liana-fashion",
    tenantId: "userf978a4e8-dc12-4610-af43-c6f602566b8f",
    apiBaseUrl: sharedApiBaseUrl,
    storageBaseUrl: sharedStorageBaseUrl,
    brandName: "Liana Fashion",
    primaryColor: "#2563eb",
    secondaryColor: "#111827",
    linkedWithUltra: true,
    showQuantity: true,
    contact: DEFAULT_CONTACT_INFO,
    checkout: Object.freeze({
      provider: "api",
      whatsapp: Object.freeze({
        phoneNumber: 96171940445,
        ...DEFAULT_CHECKOUT_WHATSAPP_DISPLAY,
      }),
      defaults: DEFAULT_CHECKOUT_ORDER_DEFAULTS,
    }),
  }),
  "warde-myw": Object.freeze({
    clientId: "warde-myw",
    tenantId: "usera3a0d69c-30ad-44b1-b2d1-9d49fe2a373e",
    apiBaseUrl: sharedApiBaseUrl,
    storageBaseUrl: sharedStorageBaseUrl,
    brandName: "Warde MYW",
    primaryColor: "#00b33c",
    secondaryColor: "#0f172a",
    linkedWithUltra: false,
    showQuantity: false,
    contact: DEFAULT_CONTACT_INFO,
    checkout: Object.freeze({
      provider: "whatsapp",
      whatsapp: Object.freeze({
        phoneNumber: "96171175687",
        ...DEFAULT_CHECKOUT_WHATSAPP_DISPLAY,
        displayInitials: "MYW",
        displayName: "MYW",
        displayPhoneText: "71-175-687",
      }),
      defaults: DEFAULT_CHECKOUT_ORDER_DEFAULTS,
    }),
  }),
  "custom-tenant-2": Object.freeze({
    clientId: "custom-tenant-2",
    tenantId: "replace-with-tenant-id-2",
    apiBaseUrl: sharedApiBaseUrl,
    storageBaseUrl: sharedStorageBaseUrl,
    brandName: "Custom Tenant 2",
    primaryColor: "#22c55e",
    secondaryColor: "#052e16",
    linkedWithUltra: false,
    showQuantity: false,
    contact: DEFAULT_CONTACT_INFO,
    checkout: Object.freeze({
      provider: "whatsapp",
      whatsapp: Object.freeze({
        phoneNumber: DEFAULT_WHATSAPP_PHONE_NUMBER,
        ...DEFAULT_CHECKOUT_WHATSAPP_DISPLAY,
      }),
      defaults: DEFAULT_CHECKOUT_ORDER_DEFAULTS,
    }),
  }),
  "custom-tenant-3": Object.freeze({
    clientId: "custom-tenant-3",
    tenantId: "replace-with-tenant-id-3",
    apiBaseUrl: sharedApiBaseUrl,
    storageBaseUrl: sharedStorageBaseUrl,
    brandName: "Custom Tenant 3",
    primaryColor: "#a855f7",
    secondaryColor: "#3b0764",
    linkedWithUltra: false,
    showQuantity: false,
    contact: DEFAULT_CONTACT_INFO,
    checkout: Object.freeze({
      provider: "whatsapp",
      whatsapp: Object.freeze({
        phoneNumber: DEFAULT_WHATSAPP_PHONE_NUMBER,
        ...DEFAULT_CHECKOUT_WHATSAPP_DISPLAY,
      }),
      defaults: DEFAULT_CHECKOUT_ORDER_DEFAULTS,
    }),
  }),
});

export const DOMAIN_TO_CLIENT_ID = Object.freeze({
  localhost: "liana-fashion",
  "127.0.0.1": "liana-fashion",
  "192.168.2.4": "liana-fashion",
  "liana-fashion.local": "liana-fashion",
  "tenant1.example.com": "warde-myw",
  "tenant2.example.com": "custom-tenant-2",
  "tenant3.example.com": "custom-tenant-3",
});

export const getAvailableClientIds = () => Object.keys(CLIENT_CONFIG_MAP);

export const getClientConfig = (clientId) => {
  if (!clientId) return null;
  return CLIENT_CONFIG_MAP[clientId] || null;
};

export const isBuildTimeClientLocked = () => buildTimeClientId.length > 0;

const getHostClientId = () => {
  if (typeof window === "undefined") return "";
  return DOMAIN_TO_CLIENT_ID[window.location.hostname] || "";
};

const getDevOverrideClientId = () => {
  if (isBuildTimeClientLocked() || typeof window === "undefined") return "";

  try {
    const queryParams = new URLSearchParams(window.location.search);
    const queryClientId = (queryParams.get("clientId") || "").trim();

    if (queryClientId) {
      if (getClientConfig(queryClientId)) {
        sessionStorage.setItem(clientIdOverrideStorageKey, queryClientId);
        return queryClientId;
      }
      sessionStorage.removeItem(clientIdOverrideStorageKey);
      return "";
    }

    const storedClientId = (
      sessionStorage.getItem(clientIdOverrideStorageKey) || ""
    ).trim();
    if (storedClientId && getClientConfig(storedClientId)) {
      return storedClientId;
    }
    if (storedClientId) {
      sessionStorage.removeItem(clientIdOverrideStorageKey);
    }
  } catch {
    return "";
  }

  return "";
};

const resolveCurrentClientConfig = () => {
  const defaultConfig = getClientConfig(DEFAULT_CLIENT_ID);
  if (!defaultConfig) {
    throw new Error(
      `Missing default client config: "${DEFAULT_CLIENT_ID}". ` +
        `Configured IDs: ${getAvailableClientIds().join(", ")}`
    );
  }

  if (isBuildTimeClientLocked()) {
    const buildConfig = getClientConfig(buildTimeClientId);
    if (!buildConfig) {
      throw new Error(
        `Invalid REACT_APP_CLIENT_ID: "${buildTimeClientId}". ` +
          `Valid IDs: ${getAvailableClientIds().join(", ")}`
      );
    }
    return buildConfig;
  }

  const overrideClientId = getDevOverrideClientId();
  if (overrideClientId) {
    const overrideConfig = getClientConfig(overrideClientId);
    if (overrideConfig) return overrideConfig;
  }

  const hostClientId = getHostClientId();
  if (hostClientId) {
    const hostConfig = getClientConfig(hostClientId);
    if (hostConfig) return hostConfig;
  }

  return defaultConfig;
};

const currentClientConfig = resolveCurrentClientConfig();

export const getCurrentClientConfig = () => currentClientConfig;

export const isUltraLinked = () =>
  Boolean(currentClientConfig?.linkedWithUltra);

export const shouldShowQuantity = () =>
  currentClientConfig?.showQuantity !== false;

export const buildStorageUrl = (path = "") => {
  if (typeof path === "string" && /^https?:\/\//i.test(path)) {
    return path;
  }

  const baseUrl = (currentClientConfig.storageBaseUrl || "").replace(/\/+$/, "");
  const relativePath = String(path || "").replace(/^\/+/, "");

  if (!baseUrl) {
    return relativePath ? `/${relativePath}` : "";
  }
  if (!relativePath) {
    return baseUrl;
  }
  return `${baseUrl}/${relativePath}`;
};
